import { useState, useEffect } from "react";
import Pencil from "../../assets/svg/Pencil";
import Eraser from "../../assets/svg/Eraser";
import Text from "../../assets/svg/Text";
import Capture from "../../assets/svg/Capture";
import Down from "../../assets/svg/Down";
import LaserPointer from "../../assets/svg/LaserPointer";
import Rectangle from "../../assets/svg/Rectangle";
import Circle from "../../assets/svg/Circle";
import Point from "../../assets/svg/Point";
import Line from "../../assets/svg/Line";
import FreeLine from "../../assets/svg/FreeLine";
import StyledTooltip from "../StyledTooltip";
import { Button, ConfigProvider, Flex, Select } from "antd";
import { MinusOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import IntlMessages from "src/util/IntlMessages";
import CaptureImage from "../../assets/svg/CaptureImage";

const modeOptions = [
  { key: "annotation", label: <IntlMessages id="vlink.stream.drawing.draw" />, icon: Pencil },
  { key: "text", label: <IntlMessages id="vlink.stream.drawing.text" />, icon: Text },
  { key: "eraser", label: <IntlMessages id="vlink.stream.drawing.eraser" />, icon: Eraser },
  { key: "pointer", label: <IntlMessages id="vlink.stream.drawing.laserPointer" />, icon: LaserPointer },
];

const fontSizeOptions = [8, 10, 12, 14, 16, 18, 20, 24, 28, 32];
const lineWidthOptions = [5, 10, 15, 20, 25, 30];

const isEmpty = (obj) => Object.keys(obj).length === 0;


const DrawingButton = ({
  style,
  mode,
  onChangeMode,
  color,
  onChangeColor,
  lineWidth,
  onChangeLineWidth,
  textSize,
  onChangeTextSize,
  textColor,
  onChangeTextColor,
  onDeleteAll,
  textWeight,
  textStyle,
  onChangeTextWeight,
  onChangeTextStyle,
  onCapture,
  pointerColor,
  onChangePointerColor,
  annotationInfo,
  onChangeAnnotation,
}) => {
  const [openMenus, setOpenMenus] = useState(null);
  const [hoveredItem, setHoveredItem] = useState(null);

  const colors = ["red", "yellow", "green", "blue", "black"];

  useEffect(() => {
    if (isEmpty(mode)) {
      setOpenMenus(null)
    }
  }, [mode])

  const handleModeClick = (key) => {
    const isActive = !!mode[key];
    onChangeMode({ [key]: !isActive });
    setOpenMenus(!isActive ? key : null);
  };

  const handleDownClick = (key) => {
    const isMenuOpen = openMenus === key;
    onChangeMode({ [key]: true });
    setOpenMenus(isMenuOpen ? null : key);
  };

  const getNextLineWidth = (current, direction) => {
    const currentIndex = lineWidthOptions.indexOf(current);
    if (currentIndex === -1) return current; // 만약 리스트에 없는 값이면 변경 안 함
    const nextIndex = currentIndex + direction;
    return lineWidthOptions[Math.max(0, Math.min(nextIndex, lineWidthOptions.length - 1))];
  };

  const ColorPicker = ({ colors, selectedColor, onChangeColor }) => {

    return (
      <div style={{ display: "flex", gap: "8px", justifyContent: "space-around" }}>
        {colors.map((color) => (
          <div
            key={color}
            onClick={() => onChangeColor(color)}
            style={{
              width: "20px",
              height: "20px",
              borderRadius: "50%",
              backgroundColor: color,
              border: selectedColor === color ? "2px solid white" : "none",
              outline: selectedColor === color ? "2px solid black" : "none",
              cursor: "pointer"
            }}
          />
        ))}
      </div>
    );
  };

  const subMenu = (key) => {
    // if (key !== 'pencil' && key !== 'text' && key !== 'eraser') return null;
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", justifyContent: "center" }}>
        {key === 'text' && (
          <>
            <Flex gap={4}>

              <Select
                value={textSize}
                onChange={onChangeTextSize}
                style={{ width: 64, textAlign: "right" }}
              >
                {fontSizeOptions.map((size) => (
                  <Select.Option key={size} value={size} style={{
                    textAlign: "right", marginRight: 5,
                    fontVariantNumeric: "tabular-nums",
                  }}>
                    {size}
                  </Select.Option>
                ))}
              </Select>
              <Button
                onClick={() => onChangeTextWeight(textWeight === "bold" ? "normal" : "bold")}
                style={{
                  fontWeight: "bold",
                  width: 36,
                  padding: 0,
                  backgroundColor: textWeight === "bold" ? "#FFE9EF" : "transparent",
                  color: textWeight === "bold" ? "#DF043A" : "rgba(0, 0, 0, 0.88)",
                }}
              >B</Button>
              <Button
                onClick={() => onChangeTextStyle(textStyle === "italic" ? "normal" : "italic")}
                style={{
                  fontStyle: 'italic',
                  fontWeight: "bold",
                  width: 36,
                  padding: 0,
                  backgroundColor: textStyle === "italic" ? "#FFE9EF" : "transparent",
                  color: textStyle === "italic" ? "#DF043A" : "rgba(0, 0, 0, 0.88)",
                }}
              >
                I
              </Button>
            </Flex>
            <ColorPicker colors={colors} selectedColor={textColor} onChangeColor={onChangeTextColor} />
          </>
        )}
        {key === 'annotation' && (
          <Flex vertical gap={8}	>
            <Flex justify="space-around" gap={2} align="center">
              <Button
                onClick={() => onChangeLineWidth(getNextLineWidth(lineWidth, -1))}
                size="small"
                disabled={lineWidth === lineWidthOptions[0]}
                icon={<MinusOutlined />}
              />
              <Select
                value={lineWidth}
                onChange={onChangeLineWidth}
                style={{ width: 64, textAlign: "right" }}
              >
                {lineWidthOptions.map((size) => (
                  <Select.Option key={size} value={size} style={{
                    textAlign: "right", marginRight: 5,
                    fontVariantNumeric: "tabular-nums",
                  }}>
                    {size}
                  </Select.Option>
                ))}
              </Select>
              <Button
                onClick={() => onChangeLineWidth(getNextLineWidth(lineWidth, 1))}
                size="small"
                disabled={lineWidth === lineWidthOptions[lineWidthOptions.length - 1]}
                icon={<PlusOutlined />}
              />
            </Flex>
            <Flex justify="space-around" gap={2}>

              <Button icon={<FreeLine color={annotationInfo.type === "freeLine" ? "#DF043A" : null} />}
                style={{
                  width: 28,
                  backgroundColor: annotationInfo.type === "freeLine" ? "#FFE9EF" : "transparent",
                  color: annotationInfo.type === "freeLine" ? "#DF043A" : "rgba(0, 0, 0, 0.88)",
                }}
                onClick={() => onChangeAnnotation({ ...annotationInfo, type: 'freeLine' })}

              />
              <Button icon={<Line color={annotationInfo.type === "line" ? "#DF043A" : null} />}
                style={{
                  width: 28,
                  backgroundColor: annotationInfo.type === "line" ? "#FFE9EF" : "transparent",
                  color: annotationInfo.type === "line" ? "#DF043A" : "rgba(0, 0, 0, 0.88)",
                }}
                onClick={() => onChangeAnnotation({ ...annotationInfo, type: 'line' })}

              />
              <Button icon={<Rectangle color={annotationInfo.type === "rectangle" ? "#DF043A" : null} />}
                style={{
                  width: 28,
                  backgroundColor: annotationInfo.type === "rectangle" ? "#FFE9EF" : "transparent",
                  color: annotationInfo.type === "rectangle" ? "#DF043A" : "rgba(0, 0, 0, 0.88)",
                }}
                onClick={() => onChangeAnnotation({ ...annotationInfo, type: 'rectangle' })}
              />
              <Button icon={<Circle color={annotationInfo.type === "circle" ? "#DF043A" : null} />}
                style={{
                  width: 28,
                  backgroundColor: annotationInfo.type === "circle" ? "#FFE9EF" : "transparent",
                  color: annotationInfo.type === "circle" ? "#DF043A" : "rgba(0, 0, 0, 0.88)",
                }}
                onClick={() => onChangeAnnotation({ ...annotationInfo, type: 'circle' })}
              />
              <Button icon={<Point color={annotationInfo.type === "point" ? "#DF043A" : null} />}
                style={{
                  width: 28,
                  backgroundColor: annotationInfo.type === "point" ? "#FFE9EF" : "transparent",
                  color: annotationInfo.type === "point" ? "#DF043A" : "rgba(0, 0, 0, 0.88)",
                }}
                onClick={() => onChangeAnnotation({ ...annotationInfo, type: 'point' })}
              />
            </Flex>
            <ColorPicker colors={colors} selectedColor={annotationInfo.color} onChangeColor={(color) => onChangeAnnotation({ ...annotationInfo, color })} />

          </Flex>
        )}
        {key === 'eraser' && (
          <Button
            size="large"
            icon={<DeleteOutlined />}
            style={{ border: "none" }}
            onClick={onDeleteAll}
          ><span><IntlMessages id="vlink.button.clearAll" /></span></Button>

        )}
        {key === 'pointer' && (
          <ColorPicker
            colors={colors.slice(0, -1)}
            selectedColor={pointerColor}
            onChangeColor={(color) => {
              onChangePointerColor(color);
              setOpenMenus(null);
            }}
          />
        )}
      </div>
    );
  };

  return (
    <div style={{ display: "flex", alignItems: "center", cursor: "pointer", ...style, padding: "5px", borderRadius: "5px" }}>
      <div style={{ display: "flex", gap: "2px", marginLeft: "5px", height: "100%" }}>
        <ConfigProvider theme={{
          components: {
            Button: {
              defaultColor: '#4F4F4F',
              defaultHoverColor: "#4f4f4f",
              defaultActiveColor: "#DF043A"
            },
          },
        }}>
          <StyledTooltip content={<IntlMessages id="vlink.stream.drawing.capture" />} trigger="hover" zIndex="999">
            <Button
              size="large"
              icon={<CaptureImage style={{ transform: "scale(1.6)" }} />}
              style={{
                width: 49,
                height: "100%",
                border: "none",
                backgroundColor: hoveredItem === 'capture' ? "#FFE9EF" : "transparent",
              }}
              onClick={onCapture}
              onMouseEnter={() => setHoveredItem("capture")}
              onMouseLeave={() => setHoveredItem(null)}
            />
          </StyledTooltip>
        </ConfigProvider>
        {modeOptions.map(({ key, icon: Icon, label }) => {
          const isActive = !!mode[key];
          return (
            <StyledTooltip
              content={subMenu(key)}
              open={openMenus === key}
              onOpenChange={(visible) => {
                if (!visible) {
                  setOpenMenus(null);
                }
              }}
              keyboard={false}
              key={key}
              zIndex="99"
            >
              <StyledTooltip content={isActive? "":label} trigger="hover" >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    padding: "5px",
                    borderRadius: "5px",
                    width: 54,
                    backgroundColor: hoveredItem === key ? "#FFE9EF" : "transparent",
                    transition: "background-color 0.2s ease",
                    justifyContent: "center",
                    cursor: "pointer"
                  }}
                  onMouseEnter={() => setHoveredItem(key)}
                  onMouseLeave={() => setHoveredItem(null)}
                  onClick={() => handleModeClick(key)}
                >
                  <Icon style={{ transform: "scale(1.8)" }} color={isActive ? "#DF043A" : "#4F4F4F"} />
                  <div
                    style={{
                      width: "12px",
                      height: "24px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  // onClick={(e) => {
                  //   e.stopPropagation();
                  //   handleDownClick(key);
                  // }}
                  >
                    <Down style={{ fontSize: "18px" }} color={isActive ? "#DF043A" : "#4F4F4F"} />
                  </div>
                </div>
              </StyledTooltip>
            </StyledTooltip>
          );
        })}
      </div>
    </div>
  );
};

export default DrawingButton;
